/*
 * star-readings-lp.js
 * Interactivity for the Star Readings landing page (sections/star-readings-lp.liquid).
 * Vanilla JS, no deps. Handles: tier tab selection (+ checklist swap), birth-time
 * toggle, FAQ accordion, sticky mobile bar, and add-to-cart (adds the tier product,
 * saves birth details as cart/order attributes) -> /cart.
 */
(function () {
  const root = document.querySelector('[data-srlp]');
  if (!root) return;

  const routes = window.routes || {};
  const cartAddUrl = (root.dataset.cartAddUrl || routes.cart_add_url || '/cart/add') + '.js';
  const cartUpdateUrl = (root.dataset.cartUpdateUrl || routes.cart_update_url || '/cart/update') + '.js';
  const cartUrl = root.dataset.cartUrl || routes.cart_url || '/cart';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' };

  // --- tier selection ---------------------------------------------------------
  let selectedTier = root.dataset.defaultTier || 'complete';

  function applyTier(tier) {
    selectedTier = tier;
    const order = { blueprint: 0, complete: 1, lifetime: 2 };
    const idx = order[tier] ?? 1;

    // tabs
    root.querySelectorAll('[data-tier-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.tierTab === tier ? 'true' : 'false');
    });
    // tier detail panels (name/sub/price + per-tier reveal list)
    root.querySelectorAll('[data-tier-detail]').forEach((panel) => {
      panel.hidden = panel.dataset.tierDetail !== tier;
    });
    // checklist rows: included if row's minTier <= selected index
    root.querySelectorAll('[data-checklist-row]').forEach((rowEl) => {
      const min = parseInt(rowEl.dataset.checklistRow, 10);
      const inc = min <= idx;
      rowEl.classList.toggle('is-included', inc);
      rowEl.classList.toggle('is-excluded', !inc);
      const check = rowEl.querySelector('[data-row-check]');
      const dash = rowEl.querySelector('[data-row-dash]');
      const txt = rowEl.querySelector('[data-row-text]');
      const tag = rowEl.querySelector('[data-row-tag]');
      if (check) check.hidden = !inc;
      if (dash) dash.hidden = inc;
      if (txt) {
        txt.style.textDecoration = inc ? 'none' : 'line-through';
        txt.style.color = inc ? 'var(--ink)' : 'var(--ink-40)';
      }
      if (tag) tag.hidden = inc;
    });
    // "unlock everything" upgrade link points at the next tier above the selection
    const upgrade = root.querySelector('[data-checklist-upgrade]');
    if (upgrade) {
      const rows = [...root.querySelectorAll('[data-checklist-row]')].map((r) => parseInt(r.dataset.checklistRow, 10));
      const missing = rows.filter((m) => m > idx);
      if (missing.length) {
        const nextIdx = Math.min(...missing);
        const nextName = upgrade.dataset['tierName' + nextIdx] || '';
        const nextPrice = upgrade.dataset['tierPrice' + nextIdx] || '';
        const nextId = ['blueprint', 'complete', 'lifetime'][nextIdx];
        upgrade.hidden = false;
        const label = upgrade.querySelector('[data-upgrade-label]');
        if (label) label.textContent = 'Unlock everything above with ' + nextName + ' · ' + nextPrice;
        upgrade.dataset.upgradeTo = nextId;
      } else {
        upgrade.hidden = true;
      }
    }
    // CTA price labels (inline + sticky bar)
    const pricePanel = root.querySelector('[data-tier-detail="' + tier + '"]');
    const tierPrice = pricePanel ? pricePanel.dataset.price : '';
    root.querySelectorAll('[data-cta-price]').forEach((el) => { el.textContent = tierPrice; });
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (stickyPrice && tierPrice) stickyPrice.textContent = tierPrice;
  }

  root.querySelectorAll('[data-tier-tab]').forEach((tab) => {
    tab.addEventListener('click', () => applyTier(tab.dataset.tierTab));
  });
  const upgradeBtn = root.querySelector('[data-checklist-upgrade]');
  if (upgradeBtn) upgradeBtn.addEventListener('click', () => applyTier(upgradeBtn.dataset.upgradeTo));

  // --- collapse the long reveals list on phones (CSS hides rows 6+ when collapsed) ---
  const tierDetail = root.querySelector('.tier-detail');
  const revealsToggle = root.querySelector('[data-reveals-toggle]');
  if (tierDetail && revealsToggle) {
    const total = tierDetail.querySelectorAll('[data-checklist-row]').length;
    if (total > 5) {
      const moreLabel = 'See everything your chart reveals';
      const lessLabel = 'Show less';
      tierDetail.setAttribute('data-reveals-collapsed', '');
      revealsToggle.textContent = moreLabel;
      revealsToggle.addEventListener('click', () => {
        const collapsed = tierDetail.toggleAttribute('data-reveals-collapsed');
        revealsToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        revealsToggle.textContent = collapsed ? moreLabel : lessLabel;
      });
    } else {
      revealsToggle.hidden = true;
    }
  }

  // --- birth-time toggle (disables the hour / minute / am-pm selects) ---------
  const timeToggle = root.querySelector('[data-time-toggle]');
  const timeSelects = [].slice.call(root.querySelectorAll('[data-time-hour],[data-time-min],[data-time-ampm]'));
  if (timeToggle) {
    timeToggle.addEventListener('click', () => {
      const pressed = timeToggle.getAttribute('aria-pressed') === 'true';
      const next = !pressed; // pressed = "I don't know my exact birth time"
      timeToggle.setAttribute('aria-pressed', next ? 'true' : 'false');
      timeSelects.forEach((s) => { s.disabled = next; if (next) s.value = ''; });
    });
  }

  // --- "I'll add these later" toggle: collapse the birth-detail fields so the
  //     customer can order now and send date/time/place by email afterwards -----
  const laterToggle = root.querySelector('[data-birth-later-toggle]');
  const birthCollapse = root.querySelector('[data-birth-collapse]');
  const laterNote = root.querySelector('[data-birth-later-note]');
  if (laterToggle) {
    laterToggle.addEventListener('click', () => {
      const on = laterToggle.getAttribute('aria-pressed') !== 'true';
      laterToggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (birthCollapse) birthCollapse.hidden = on;
      if (laterNote) laterNote.hidden = !on;
    });
  }

  // --- place of birth autocomplete (OpenStreetMap / Photon, keyless) ----------
  const placeInput = root.querySelector('[data-place-input]');
  const placeList = root.querySelector('[data-place-suggest]');
  const placeHint = root.querySelector('[data-place-hint]');
  if (placeInput && placeList) {
    let debounce, controller, results = [], active = -1;
    const label = (p) => [p.name, p.state, p.country].filter(Boolean).join(', ');
    const closeList = () => { placeList.hidden = true; placeList.textContent = ''; results = []; active = -1; placeInput.setAttribute('aria-expanded', 'false'); };
    const highlight = () => { [].slice.call(placeList.children).forEach((li, i) => li.setAttribute('aria-selected', i === active ? 'true' : 'false')); };
    const choose = (i) => { const f = results[i]; if (!f) return; placeInput.value = label(f.properties || {}); closeList(); };
    const draw = (features) => {
      results = features;
      placeList.textContent = '';
      if (!features.length) { closeList(); return; }
      features.forEach((f, i) => {
        const p = f.properties || {};
        const sub = [p.state, p.country].filter(Boolean).join(', ');
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.dataset.i = i;
        const name = document.createElement('span');
        name.className = 'ps-name';
        name.textContent = p.name; // textContent escapes API content
        li.appendChild(name);
        if (sub) {
          const s = document.createElement('span');
          s.className = 'ps-sub';
          s.textContent = sub;
          li.appendChild(s);
        }
        placeList.appendChild(li);
      });
      placeList.hidden = false;
      if (placeHint) placeHint.hidden = false;
      placeInput.setAttribute('aria-expanded', 'true');
      active = -1;
    };
    const search = (q) => {
      if (controller) controller.abort();
      controller = new AbortController();
      fetch('https://photon.komoot.io/api/?q=' + encodeURIComponent(q) + '&limit=6&lang=en&osm_tag=place', { signal: controller.signal })
        .then((r) => r.json())
        .then((d) => draw((d.features || []).filter((f) => f.properties && f.properties.name)))
        .catch(() => {});
    };
    placeInput.addEventListener('input', () => {
      const q = placeInput.value.trim();
      clearTimeout(debounce);
      if (q.length < 3) { closeList(); return; }
      debounce = setTimeout(() => search(q), 220);
    });
    placeList.addEventListener('mousedown', (e) => {
      const li = e.target.closest('li[data-i]');
      if (!li) return;
      e.preventDefault(); // keep focus, beat the blur
      choose(parseInt(li.dataset.i, 10));
    });
    placeInput.addEventListener('keydown', (e) => {
      if (placeList.hidden || !results.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, results.length - 1); highlight(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, 0); highlight(); }
      else if (e.key === 'Enter') { if (active >= 0) { e.preventDefault(); choose(active); } }
      else if (e.key === 'Escape') { closeList(); }
    });
    placeInput.addEventListener('blur', () => setTimeout(closeList, 150));
  }

  // --- FAQ accordion ----------------------------------------------------------
  root.querySelectorAll('[data-faq-row]').forEach((rowEl, i) => {
    const q = rowEl.querySelector('[data-faq-q]');
    if (!q) return;
    if (i === 0) rowEl.setAttribute('aria-expanded', 'true');
    q.addEventListener('click', () => {
      const open = rowEl.getAttribute('aria-expanded') === 'true';
      root.querySelectorAll('[data-faq-row]').forEach((r) => r.setAttribute('aria-expanded', 'false'));
      rowEl.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  });

  // --- sticky mobile bar ------------------------------------------------------
  // "Choose My Reading" (scroll to the buy box) until the buy box is on screen,
  // then it morphs into "Add to Cart" — the real action — so the CTA is always
  // one tap away through the long form. Shown only on phones (.srlp-mobile-only).
  const scrollToBuy = () => {
    const buy = root.querySelector('#srlp-buy');
    if (buy) window.scrollTo({ top: buy.getBoundingClientRect().top + window.scrollY - 64, behavior: 'smooth' });
  };
  const sticky = document.querySelector('[data-srlp-sticky]');
  const hero = root.querySelector('[data-hero]');
  if (sticky && hero) {
    const buy = root.querySelector('#srlp-buy');
    const stickyCta = sticky.querySelector('[data-sticky-cta]');
    const stickyPrice = sticky.querySelector('[data-sticky-price]');
    const onScroll = () => {
      const pastHero = window.scrollY > hero.offsetTop + hero.offsetHeight - 80;
      let buyOnScreen = false;
      if (buy) {
        const r = buy.getBoundingClientRect();
        buyOnScreen = r.bottom > 70 && r.top < window.innerHeight - 60;
      }
      sticky.classList.toggle('is-visible', pastHero);
      sticky.setAttribute('aria-hidden', pastHero ? 'false' : 'true');
      sticky.dataset.mode = buyOnScreen ? 'buy' : 'scroll';
      if (stickyCta) stickyCta.textContent = buyOnScreen ? 'Add to Cart' : 'Choose My Reading';
      // price stays out of the landing experience — only show it at the buy box
      if (stickyPrice) stickyPrice.style.display = buyOnScreen ? '' : 'none';
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (stickyCta) {
      stickyCta.addEventListener('click', (e) => {
        e.preventDefault();
        if (sticky.dataset.mode === 'buy') {
          const addBtn = root.querySelector('[data-add-to-cart]');
          if (addBtn) addBtn.click();
        } else {
          scrollToBuy();
        }
      });
    }
  }

  // --- in-page "Get My Reading" CTAs scroll to the buy box --------------------
  root.querySelectorAll('[data-scroll-buy]').forEach((el) => {
    el.addEventListener('click', (e) => { e.preventDefault(); scrollToBuy(); });
  });

  // --- add to cart ------------------------------------------------------------
  // Birth details are saved as CART (order) attributes, not line-item properties,
  // so they live on the order and are readable on the cart page / in the order.
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  function formatDob(v) {
    const m = (v || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return v;
    return parseInt(m[3], 10) + ' ' + (MONTHS[parseInt(m[2], 10) - 1] || m[2]) + ' ' + m[1];
  }
  function dobIso(d, m, y) {
    if (!d || !m || !y) return '';
    const pad = (n) => (String(n).length < 2 ? '0' + n : String(n));
    return y + '-' + pad(m) + '-' + pad(d);
  }
  function gatherAttributes() {
    const attrs = {};
    const focus = root.querySelector('[data-prop="focus"]');
    const laterOn = laterToggle && laterToggle.getAttribute('aria-pressed') === 'true';
    if (laterOn) {
      // Opted to send birth details by email later. Flag it so the confirmation
      // email asks for them and the order is filterable as "awaiting details".
      attrs['Birth details'] = 'To follow';
    } else {
      const dDay = root.querySelector('[data-dob-day]');
      const dMon = root.querySelector('[data-dob-month]');
      const dYr = root.querySelector('[data-dob-year]');
      const place = root.querySelector('[data-prop="place"]');
      const dobValue = dobIso(dDay && dDay.value, dMon && dMon.value, dYr && dYr.value);
      if (dobValue) attrs['Birth date'] = formatDob(dobValue);
      // time: the "I don't know" toggle wins; otherwise need at least hour + am/pm
      if (timeToggle && timeToggle.getAttribute('aria-pressed') === 'true') {
        attrs['Birth time'] = 'Not known';
      } else {
        const h = root.querySelector('[data-time-hour]');
        const mi = root.querySelector('[data-time-min]');
        const ap = root.querySelector('[data-time-ampm]');
        if (h && h.value && ap && ap.value) {
          attrs['Birth time'] = h.value + ':' + ((mi && mi.value) ? mi.value : '00') + ' ' + ap.value;
        }
      }
      if (place && place.value.trim()) attrs['Birth place'] = place.value.trim();
    }
    if (focus && focus.value.trim()) attrs['Focus'] = focus.value.trim();
    return attrs;
  }

  const addBtn = root.querySelector('[data-add-to-cart]');
  if (addBtn) {
    addBtn.addEventListener('click', async () => {
      const panel = root.querySelector('[data-tier-detail="' + selectedTier + '"]');
      const variantId = panel && panel.dataset.variantId;
      const errEl = root.querySelector('[data-add-error]');
      if (!variantId) {
        // no product configured for this tier; tell the visitor instead of failing silently
        console.warn('[star-readings-lp] no product configured for tier:', selectedTier);
        if (errEl) { errEl.textContent = 'This reading is not available right now. Please try another option.'; errEl.hidden = false; }
        return;
      }
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      if (errEl) errEl.hidden = true;
      try {
        // 1) add the chosen tier product to the cart
        await fetch(cartAddUrl, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
        });
        // 2) write the birth details as cart/order attributes
        await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ attributes: gatherAttributes() }),
        });
        window.location.href = cartUrl;
      } catch (err) {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-busy');
        if (errEl) { errEl.textContent = 'Something went wrong. Please try again.'; errEl.hidden = false; }
      }
    });
  }

  applyTier(selectedTier);
})();
